import { Button, Col, Form, Row } from "react-bootstrap";

import {
  createEmptyFromSample,
  humanizeKey,
  isImageFieldKey,
  shouldUseMediaForStringArray,
} from "../utils/content.utils";
import MediaField from "./MediaField";

const LOCKED_KEYS = new Set(["id", "_id"]);

const isLongText = (key, value) => {
  const k = String(key || "").toLowerCase();
  if (
    k.includes("description") ||
    k.includes("paragraph") ||
    k.includes("subtitle") ||
    k.includes("subTitle") ||
    k === "copyrighttext"
  ) {
    return true;
  }
  return typeof value === "string" && value.length > 120;
};

const PrimitiveField = ({
  label,
  fieldKey,
  value,
  disabled,
  onChange,
}) => {
  if (typeof value === "boolean") {
    return (
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id={`content-bool-${fieldKey}-${label}`}
          label={label}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
      </Form.Group>
    );
  }

  if (typeof value === "number") {
    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <Form.Control
          type="number"
          value={Number.isFinite(value) ? value : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(0);
              return;
            }
            const next = Number(raw);
            if (Number.isFinite(next)) onChange(next);
          }}
          disabled={disabled}
        />
      </Form.Group>
    );
  }

  const asTextarea = isLongText(fieldKey, value);
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        as={asTextarea ? "textarea" : "input"}
        rows={asTextarea ? 3 : undefined}
        type={asTextarea ? undefined : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </Form.Group>
  );
};

export const ValueEditor = ({
  label,
  fieldKey,
  value,
  disabled,
  onChange,
  onUploadStart,
  onUploadEnd,
  depth = 0,
}) => {
  if (isImageFieldKey(fieldKey) && (typeof value === "string" || value == null)) {
    return (
      <MediaField
        label={label}
        value={value || ""}
        disabled={disabled}
        onChange={onChange}
        onUploadStart={onUploadStart}
        onUploadEnd={onUploadEnd}
      />
    );
  }

  if (Array.isArray(value)) {
    return (
      <ArrayFieldEditor
        label={label}
        fieldKey={fieldKey}
        items={value}
        disabled={disabled}
        onChange={onChange}
        onUploadStart={onUploadStart}
        onUploadEnd={onUploadEnd}
        depth={depth}
      />
    );
  }

  if (value && typeof value === "object") {
    return (
      <ObjectFieldEditor
        label={label}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onUploadStart={onUploadStart}
        onUploadEnd={onUploadEnd}
        depth={depth}
        hideId
      />
    );
  }

  return (
    <PrimitiveField
      label={label}
      fieldKey={fieldKey}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

export const ObjectFieldEditor = ({
  label,
  value = {},
  disabled = false,
  onChange,
  onUploadStart,
  onUploadEnd,
  depth = 0,
  hideId = true,
  excludeKeys = [],
}) => {
  const entries = Object.entries(value || {}).filter(([key]) => {
    if (excludeKeys.includes(key)) return false;
    if (hideId && LOCKED_KEYS.has(key)) return false;
    return true;
  });

  if (!entries.length) {
    return label ? (
      <div className="mb-3">
        <Form.Label className="text-muted">{label}</Form.Label>
        <p className="text-muted small mb-0">No editable fields</p>
      </div>
    ) : null;
  }

  const updateKey = (key, nextValue) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <div className={depth > 0 ? "border rounded p-3 mb-3" : "mb-2"}>
      {label && depth > 0 ? <h6 className="mb-3">{label}</h6> : null}
      <Row>
        {entries.map(([key, child]) => {
          const colSize =
            isImageFieldKey(key) ||
            Array.isArray(child) ||
            (child && typeof child === "object")
              ? 12
              : isLongText(key, child)
                ? 12
                : 6;

          return (
            <Col md={colSize} key={key}>
              <ValueEditor
                label={humanizeKey(key)}
                fieldKey={key}
                value={child}
                disabled={disabled}
                onChange={(next) => updateKey(key, next)}
                onUploadStart={onUploadStart}
                onUploadEnd={onUploadEnd}
                depth={depth + 1}
              />
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export const ArrayFieldEditor = ({
  label,
  fieldKey,
  items = [],
  disabled = false,
  onChange,
  onUploadStart,
  onUploadEnd,
  depth = 0,
}) => {
  const list = Array.isArray(items) ? items : [];
  const useMedia = shouldUseMediaForStringArray(fieldKey, list);
  const sample = list.find((item) => item != null);

  const updateItem = (index, nextValue) => {
    const next = list.map((item, i) => (i === index ? nextValue : item));
    onChange(next);
  };

  const addItem = () => {
    if (useMedia || typeof sample === "string" || sample == null) {
      onChange([...list, ""]);
      return;
    }
    onChange([...list, createEmptyFromSample(sample)]);
  };

  const removeItem = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0">{label}</Form.Label>
        <Button
          type="button"
          size="sm"
          variant="outline-primary"
          disabled={disabled}
          onClick={addItem}
        >
          + Add
        </Button>
      </div>

      {list.length === 0 ? (
        <p className="text-muted small mb-0">No items yet.</p>
      ) : (
        list.map((item, index) => {
          const itemLabel = `${label} #${index + 1}`;

          if (useMedia) {
            return (
              <div
                key={`${fieldKey}-${index}`}
                className="border rounded p-3 mb-2"
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong className="small">{itemLabel}</strong>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    disabled={disabled}
                    onClick={() => removeItem(index)}
                  >
                    Remove
                  </Button>
                </div>
                <MediaField
                  label="Image"
                  value={typeof item === "string" ? item : ""}
                  disabled={disabled}
                  onChange={(next) => updateItem(index, next)}
                  onUploadStart={onUploadStart}
                  onUploadEnd={onUploadEnd}
                />
              </div>
            );
          }

          if (typeof item === "string" || typeof item === "number" || typeof item === "boolean" || item == null) {
            return (
              <div
                key={`${fieldKey}-${index}`}
                className="d-flex gap-2 align-items-start mb-2"
              >
                <div className="flex-grow-1">
                  <PrimitiveField
                    label={itemLabel}
                    fieldKey={`${fieldKey}-${index}`}
                    value={item ?? ""}
                    disabled={disabled}
                    onChange={(next) => updateItem(index, next)}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  className="mt-4"
                  disabled={disabled}
                  onClick={() => removeItem(index)}
                >
                  Remove
                </Button>
              </div>
            );
          }

          return (
            <div
              key={`${fieldKey}-${index}`}
              className="border rounded p-3 mb-2"
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="small">
                  {item?.title || item?.label || item?.name || itemLabel}
                </strong>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  disabled={disabled}
                  onClick={() => removeItem(index)}
                >
                  Remove
                </Button>
              </div>
              <ObjectFieldEditor
                value={item}
                disabled={disabled}
                onChange={(next) => updateItem(index, next)}
                onUploadStart={onUploadStart}
                onUploadEnd={onUploadEnd}
                depth={depth + 1}
                hideId={false}
              />
            </div>
          );
        })
      )}
    </div>
  );
};

export default ValueEditor;
